import FormDatePicker from '@/components/form-date-picker';
import commonMessages from '@/lang/messages/common';
import { sxAppend } from '@/libs/material/sx';
import { getUtcDateForMDY, getYMD } from '@/utils/date-time';
import { AccountAccount, AccountGender } from '@calm/evexia-web-sdk';
import { yupResolver } from '@hookform/resolvers/yup';
import useAccount from '@rhg/hooks/use-account';
import useCircles from '@rhg/hooks/use-circles';
import { useIsMobile } from '@rhg/hooks/use-is-mobile';
import { useLogoutConfirmation } from '@rhg/hooks/use-logout';
import { useUpdateAccount } from '@rhg/hooks/use-update-account';
import {
    Box,
    Button,
    Theme,
    Typography,
    SxProps,
    Stack,
} from '@rhg/libs/material';
import * as yup from '@rhg/libs/yup';
import { differenceInYears } from 'date-fns';
import {
    MutableRefObject,
    ReactNode,
    useCallback,
    useId,
    useRef,
    useState,
} from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { LoadingDots } from '../animations/loading-dots';
import { Heading } from '../heading';
import { RegistrationSimpleDialog } from '../registration/registration-simple-dialog';
import { AgeGenderFormActions } from './age-gender-form-actions';
import { AgeGenderFormIcon } from './age-gender-form-icon';
import { GenderSelect } from './gender-select';
import { ageGenderMessages } from './messages';
import {
    styleAddEditButtonGroup,
    styleAddEditButton,
    styleContainer,
    styleContent,
    styleCopyBlock,
    styleForm,
    styleOuterWrapper,
    styleFormOuterWrapper,
    styleVerticalSpacing,
    styleFormIconWrapper,
} from './styles';

interface AgeGenderProps {
    title?: string;
    heading?: string;
    subHeading?: string;
    sx?: SxProps<Theme>;
    primaryCtaLabel?: string;
    primaryCtaStartIcon?: ReactNode;
    secondaryCtaLabel?: string;
    showSecondaryCta?: boolean;
    onConfirm?: () => void;
    onCancel?: () => void;
    showAddEditButtons?: boolean;
}

interface AgeGenderFormState {
    dob: Date;
    gender?: AccountGender;
}

export const AgeGenderStandalone = ({
    title,
    heading,
    subHeading,
    sx,
    primaryCtaLabel,
    primaryCtaStartIcon,
    secondaryCtaLabel,
    showSecondaryCta = true,
    showAddEditButtons = false,
    onConfirm,
    onCancel,
}: AgeGenderProps) => {
    const formId = useId();
    const { formatMessage } = useIntl();
    const isSubmitDisabledRef = useRef(false);

    return (
        <Box sx={sxAppend(styleOuterWrapper, sx)}>
            <Box sx={sxAppend(styleContainer)}>
                {showAddEditButtons ? (
                    <Box sx={styleAddEditButtonGroup}>
                        {/* TODO: In EVE-8765, Add analytics to CTA buttons */}
                        <Button
                            sx={styleAddEditButton}
                            onClick={onCancel}
                            skipTracking
                        >
                            {formatMessage(commonMessages['common.cancel'])}
                        </Button>
                        <Typography variant="h5">
                            {title ?? formatMessage(ageGenderMessages.title)}
                        </Typography>
                        <Button
                            sx={styleAddEditButton}
                            type="submit"
                            skipTracking
                            form={formId}
                            disabled={isSubmitDisabledRef.current}
                        >
                            {formatMessage(commonMessages['common.save'])}
                        </Button>
                    </Box>
                ) : (
                    <Typography variant="h5">
                        {title ?? formatMessage(ageGenderMessages.title)}
                    </Typography>
                )}
                <Box sx={styleFormIconWrapper}>
                    <AgeGenderFormIcon />
                </Box>
                <AgeGenderForm
                    heading={heading}
                    subHeading={subHeading}
                    onConfirm={onConfirm}
                    formId={formId}
                    isDisabledRef={isSubmitDisabledRef}
                    sx={sx}
                />
            </Box>
            <AgeGenderFormActions
                formId={formId}
                primaryCtaLabel={primaryCtaLabel}
                primaryCtaStartIcon={primaryCtaStartIcon}
                secondaryCtaLabel={secondaryCtaLabel}
                showSecondaryCta={showSecondaryCta}
                onCancel={onCancel}
                isDisabled={isSubmitDisabledRef.current}
            />
        </Box>
    );
};

interface AgeGenderFormProps {
    /** formId must be passed to the AgeGenderFormActions component to ensure the form is submitted when the primary CTA is clicked */
    formId: string;
    /** Needed to properly disable the form CTA(s) when form is submitted */
    isDisabledRef: MutableRefObject<boolean>;
    heading?: string;
    subHeading?: string;
    formIcon?: ReactNode;
    sx?: SxProps<Theme>;
    onConfirm?: (newAccount?: AccountAccount) => void;
    willForceOver18?: boolean;
}

interface AgeGenderFormComponentProps extends AgeGenderFormProps {
    account?: AccountAccount;
    onSubmit: (account: Partial<AccountAccount>) => Promise<void>;
}

export const AgeGenderFormComponent = ({
    account,
    heading,
    subHeading,
    sx,
    onConfirm,
    formId,
    isDisabledRef,
    onSubmit,
}: AgeGenderFormComponentProps) => {
    const { formatMessage } = useIntl();
    const isMobile = useIsMobile();

    const { control, handleSubmit } = useForm<AgeGenderFormState>({
        mode: 'onSubmit',
        defaultValues: {
            // Avoid UTC date format issues by appending T00:00:00
            dob: account?.dob ? getUtcDateForMDY(account.dob) : undefined,
            gender: account?.gender ? account.gender : undefined,
        },
        resolver: yupResolver(
            yup.object({
                dob: yup
                    .date()
                    .required(formatMessage(ageGenderMessages.dobInputError)),
                gender: yup
                    .string()
                    .oneOf(Object.values(AccountGender))
                    .optional(),
            })
        ),
    });

    const _onSubmit = useCallback(
        async (ageGenderForm: AgeGenderFormState) => {
            isDisabledRef.current = true;
            const hasDobChanged = control.getFieldState('dob').isDirty;
            const hasGenderChanged = control.getFieldState('gender').isDirty;

            const formattedDate = getYMD(ageGenderForm.dob);

            await onSubmit({
                ...(hasDobChanged ? { dob: formattedDate } : {}),
                ...(hasGenderChanged ? { gender: ageGenderForm.gender } : {}),
            });
            isDisabledRef.current = false;
            onConfirm?.();
        },
        [control, isDisabledRef, onConfirm, onSubmit]
    );

    return (
        <Box sx={sxAppend(styleFormOuterWrapper, sx)}>
            <Box sx={styleContent}>
                <Box sx={styleCopyBlock}>
                    <Heading
                        titleAlign="center"
                        subtitleAlign="center"
                        titleVariant={isMobile ? 'h5' : 'h4'}
                        title={
                            heading ?? formatMessage(ageGenderMessages.heading)
                        }
                        subtitleVariant="body1"
                        subtitle={
                            subHeading ??
                            formatMessage(ageGenderMessages.subHeading)
                        }
                    />
                </Box>

                <form
                    onSubmit={handleSubmit(_onSubmit)}
                    style={styleForm}
                    id={formId}
                >
                    <FormDatePicker
                        name="dob"
                        label={formatMessage(ageGenderMessages.dobInputLabel)}
                        control={control}
                        textFieldProps={{
                            fullWidth: true,
                        }}
                    />
                    <GenderSelect control={control} sx={styleVerticalSpacing} />
                </form>
            </Box>
        </Box>
    );
};

export const AgeGenderForm = ({
    heading,
    subHeading,
    sx,
    onConfirm,
    formId,
    isDisabledRef,
    willForceOver18,
}: AgeGenderFormProps) => {
    const { account, isLoading } = useAccount();
    const { updateAccountAsync, reset: accountReset } = useUpdateAccount();
    const { refetch: refetchCircles } = useCircles();
    const [showNotAvailableDialog, setShowNotAvailableDialog] = useState(false);
    const { formatMessage } = useIntl();
    const { openLogoutDialog } = useLogoutConfirmation();

    const onSubmit = useCallback(
        async (accountChanges: Partial<AccountAccount>) => {
            if (willForceOver18) {
                const dobString = accountChanges.dob;
                if (!dobString) {
                    setShowNotAvailableDialog(true);
                    return;
                }

                const dobDate = getUtcDateForMDY(dobString);
                if (differenceInYears(new Date(), dobDate) < 18) {
                    setShowNotAvailableDialog(true);
                    return;
                }
            }

            try {
                const newAccount = await updateAccountAsync(accountChanges);
                await refetchCircles();
                onConfirm?.(newAccount);
            } catch (error) {
                if (error.status === 422) {
                    // Restricted due to user's age
                    setShowNotAvailableDialog(true);
                }
            }
        },
        [willForceOver18, onConfirm, refetchCircles, updateAccountAsync]
    );

    if (isLoading) {
        return (
            <Stack height={200} alignItems="center" justifyContent="center">
                <LoadingDots />
            </Stack>
        );
    }

    const onLogout = () => {
        accountReset();
        openLogoutDialog();
    };

    return (
        <>
            {showNotAvailableDialog && (
                <RegistrationSimpleDialog
                    onClose={() => setShowNotAvailableDialog(false)}
                    trackDialog="NotMapped"
                    title={formatMessage(
                        ageGenderMessages.notAvailableDialogTitle
                    )}
                    content={
                        <Typography variant="body1" textAlign="center">
                            {formatMessage(
                                ageGenderMessages.notAvailableDialogCopy
                            )}
                        </Typography>
                    }
                    buttons={[
                        {
                            trackButton: 'Cancel',
                            title: formatMessage(
                                commonMessages['common.cancel']
                            ),
                            onClick: () => setShowNotAvailableDialog(false),
                            variant: 'text',
                        },
                        {
                            trackButton: 'Logout',
                            title: formatMessage(
                                commonMessages['common.logout']
                            ),
                            onClick: onLogout,
                        },
                    ]}
                />
            )}
            <AgeGenderFormComponent
                account={account}
                heading={heading}
                subHeading={subHeading}
                sx={sx}
                formId={formId}
                isDisabledRef={isDisabledRef}
                onSubmit={onSubmit}
            />
        </>
    );
};
